/**
 * 并发控制工具
 * 
 * 提供批量操作的并发限制功能，防止过多请求同时发出导致服务过载。
 * 
 * @module utils/concurrency
 */

export interface BatchResult<T> {
  /** 成功的结果 */
  fulfilled: T[]
  /** 失败的错误 */
  rejected: Error[]
  /** 成功数量 */
  successCount: number
  /** 失败数量 */
  failureCount: number
  /** 总数 */
  total: number
}

/**
 * 并发限制执行器配置
 */
export interface ConcurrencyOptions {
  /** 最大并发数，默认 5 */
  concurrency?: number
  /** 每个请求之间的延迟（毫秒），默认 0 */
  delay?: number
  /** 失败后是否继续，默认 true */
  continueOnError?: boolean
}

/**
 * 使用并发限制执行批量异步操作
 * 
 * @param items - 要处理的项目列表
 * @param fn - 处理每个项目的异步函数
 * @param options - 配置选项
 * @returns 批量执行结果
 * 
 * @example
 * ```typescript
 * // 批量终止会话，最多 5 个并发
 * const result = await runBatch(
 *   sessions,
 *   async (session) => {
 *     return await api.killSession(session.id)
 *   },
 *   { concurrency: 5 }
 * )
 * logger.info(`成功: ${result.successCount}, 失败: ${result.failureCount}`)
 * ```
 */
export async function runBatch<T, R>(
  items: T[],
  fn: (item: T, index: number) => Promise<R>,
  options: ConcurrencyOptions = {}
): Promise<BatchResult<R>> {
  const {
    concurrency = 5,
    delay = 0,
    continueOnError = true,
  } = options

  const fulfilled: R[] = []
  const rejected: Error[] = []
  let activeCount = 0
  let currentIndex = 0
  const total = items.length

  // 使用 Promise 来控制并发
  return new Promise((resolve) => {
    const runNext = async (): Promise<void> => {
      // 获取下一个要处理的索引
      const index = currentIndex++
      if (index >= items.length) {
        // 没有更多项目了，检查是否所有任务都完成
        if (activeCount === 0) {
          resolve({
            fulfilled,
            rejected,
            successCount: fulfilled.length,
            failureCount: rejected.length,
            total,
          })
        }
        return
      }

      activeCount++
      const item = items[index]

      try {
        // 可选延迟
        if (delay > 0 && index > 0) {
          await sleep(delay)
        }

        const result = await fn(item, index)
        fulfilled.push(result)
      } catch (error) {
        rejected.push(error instanceof Error ? error : new Error(String(error)))
        if (!continueOnError) {
          // 停止处理，直接返回结果
          resolve({
            fulfilled,
            rejected,
            successCount: fulfilled.length,
            failureCount: rejected.length,
            total,
          })
          return
        }
      } finally {
        activeCount--
        // 启动下一个任务
        runNext()
      }
    }

    // 初始启动 concurrency 个任务
    const initialTasks = Math.min(concurrency, items.length)
    for (let i = 0; i < initialTasks; i++) {
      runNext()
    }

    // 处理空数组的情况
    if (items.length === 0) {
      resolve({
        fulfilled,
        rejected,
        successCount: 0,
        failureCount: 0,
        total: 0,
      })
    }
  })
}

/**
 * Promise.allSettled 的并发限制版本
 * 
 * @param promises - Promise 工厂函数数组
 * @param concurrency - 最大并发数
 * @returns 所有 Promise 的结果
 */
export async function allSettledWithLimit<T>(
  factories: (() => Promise<T>)[],
  concurrency: number = 5
): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = new Array(factories.length)
  let currentIndex = 0
  let activeCount = 0

  return new Promise((resolve) => {
    const runNext = async (): Promise<void> => {
      const index = currentIndex++
      if (index >= factories.length) {
        if (activeCount === 0) {
          resolve(results)
        }
        return
      }

      activeCount++

      try {
        const value = await factories[index]()
        results[index] = { status: 'fulfilled', value }
      } catch (reason) {
        results[index] = { status: 'rejected', reason }
      } finally {
        activeCount--
        runNext()
      }
    }

    const initialTasks = Math.min(concurrency, factories.length)
    for (let i = 0; i < initialTasks; i++) {
      runNext()
    }

    if (factories.length === 0) {
      resolve(results)
    }
  })
}

/**
 * 睡眠函数
 * @param ms - 毫秒数
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export default { runBatch, allSettledWithLimit, sleep }
